'use client';

import { useState, useEffect, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import CTAButton from '../landing-page/CTAbutton';
import BrandLogo from '@/components/common/BrandLogo';
import { cn } from '@/lib/utils';
import { useRouter, usePathname } from 'next/navigation';

export interface NavigationItem {
  label: string;
  anchor: string;
  description: string;
}

interface StickyHeaderNavProps {
  scrollOffset?: number;
  onSectionChange?: (sectionId: string) => void;
}

const StickyHeaderNav = ({ scrollOffset = 60, onSectionChange }: StickyHeaderNavProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [scrolled, setScrolled] = useState(false);

  const router = useRouter();
  const pathname = usePathname();

  const navigationItems: NavigationItem[] = useMemo(
    () => [
      { label: 'Process', anchor: '#process', description: '28-day development timeline' },
      { label: 'Launches', anchor: '#case-studies', description: 'Recent launches showcase' },
      { label: 'FAQs', anchor: '#faq', description: 'Frequently Asked Questions' },
      { label: 'About', anchor: '#about', description: 'About our company' },
    ],
    []
  );

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);

      const sections = navigationItems.map((item) => item.anchor.substring(1));
      const currentSection = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= scrollOffset + 100 && rect.bottom >= scrollOffset;
        }
        return false;
      });

      if (currentSection && currentSection !== activeSection) {
        setActiveSection(currentSection);
        onSectionChange?.(currentSection);
      }
    };

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const throttledScroll = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        handleScroll();
        timeoutId = null;
      }, 16);
    };

    window.addEventListener('scroll', throttledScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [activeSection, scrollOffset, onSectionChange, navigationItems]);

  const scrollToSection = (anchor: string) => {
    const id = anchor.replace('#', '');
    const element = document.getElementById(id);
    if (!element) return;
    window.scrollTo({ top: element.offsetTop - scrollOffset, behavior: 'smooth' });
  };

  const handleAnchorClick = (anchor: string) => {
    setIsMobileMenuOpen(false);
    if (pathname === '/') {
      scrollToSection(anchor);
    } else {
      router.push(`/${anchor}`);
    }
  };

  const onHero = pathname === '/' && !scrolled;

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-colors duration-300',
          onHero && !isMobileMenuOpen
            ? 'bg-transparent border-b border-transparent'
            : 'bg-background/90 backdrop-blur-md border-b border-border'
        )}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-8 sm:py-3.5 lg:px-16">
          <Link href="/" className="min-w-0 transition-opacity hover:opacity-90" aria-label="Nens Informatics home">
            <BrandLogo
              tone={onHero && !isMobileMenuOpen ? 'light' : 'dark'}
              markSize={34}
              className="text-[15px] sm:text-base"
              priority
            />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            {navigationItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleAnchorClick(item.anchor)}
                className={cn(
                  'animated-underline text-sm font-medium transition-colors',
                  onHero && !isMobileMenuOpen
                    ? activeSection === item.anchor.substring(1)
                      ? 'text-white'
                      : 'text-white/70 hover:text-white'
                    : activeSection === item.anchor.substring(1)
                      ? 'text-primary'
                      : 'text-text-primary hover:text-foreground'
                )}
                aria-label={item.description}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <button
            onClick={() => handleAnchorClick('#start-mvp')}
            className={cn(
              'hidden lg:inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition-colors',
              onHero && !isMobileMenuOpen
                ? 'bg-[var(--brand-orange)] text-[var(--brand-ink)] hover:bg-[#ffb01a]'
                : 'bg-primary text-primary-foreground hover:bg-[#6506b8]'
            )}
          >
            Build Your MVP
          </button>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="flex items-center justify-center lg:hidden"
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              className={onHero && !isMobileMenuOpen ? 'text-white' : 'text-text-primary'}
            />
          </button>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="border-t border-border bg-background lg:hidden"
            >
              <nav className="flex flex-col gap-1 px-5 py-4">
                {navigationItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => handleAnchorClick(item.anchor)}
                    className={cn(
                      'rounded-sm px-3 py-3 text-left text-sm font-medium',
                      activeSection === item.anchor.substring(1)
                        ? 'bg-muted text-primary'
                        : 'text-text-primary'
                    )}
                  >
                    {item.label}
                  </button>
                ))}
                <div className="mt-3">
                  <CTAButton
                    type="primary"
                    label="Build Your MVP"
                    icon="ArrowRightIcon"
                    onClick={() => handleAnchorClick('#start-mvp')}
                    className="w-full"
                  />
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default StickyHeaderNav;
